// src/components/Dashboard/PlayerDashboard/PlayerAwardsWidget.jsx
// Shows recent awards received by the player

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { FaMedal, FaArrowRight } from 'react-icons/fa';
import styles from './PlayerAwardsWidget.module.css';

const AWARD_ICONS = {
  'gold': '🥇',
  'silver': '🥈',
  'bronze': '🥉',
  'star': '⭐',
  'trophy': '🏆',
  'medal': '🏅',
  'fire': '🔥',
  'mvp': '👑',
  'default': '🎖️'
};

const PlayerAwardsWidget = ({ programs, selectedProgramId, userProfile, refreshTrigger }) => {
  const navigate = useNavigate();
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch awards for the player
  useEffect(() => {
    const fetchAwards = async () => {
      if (!programs || programs.length === 0 || !userProfile?._id) {
        setAwards([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        if (!token) {
          setLoading(false);
          return;
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch awards for each program
        const allAwards = [];
        for (const program of programs) {
          try {
            const response = await fetch(
              `${backendUrl}/api/awards/program/${program._id}/user/${userProfile._id}`, 
              { headers }
            );
            if (response.ok) {
              const awardsData = await response.json();
              const awardsArray = Array.isArray(awardsData) ? awardsData : awardsData.awards || [];
              awardsArray.forEach(award => {
                allAwards.push({
                  ...award,
                  programId: program._id,
                  programName: program.name
                });
              });
            }
          } catch (error) {
            // Skip failed fetches
          }
        }

        // Sort by date (newest first)
        allAwards.sort((a, b) => new Date(b.awardedAt || b.createdAt) - new Date(a.awardedAt || a.createdAt));
        setAwards(allAwards);
      } catch (error) {
        console.error('Error fetching awards:', error);
        setAwards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAwards();
  }, [programs, userProfile?._id, refreshTrigger]);

  // Filter awards by selected program
  const filteredAwards = selectedProgramId === 'all' 
    ? awards 
    : awards.filter(a => a.programId === selectedProgramId);

  const getAwardIcon = (award) => {
    const iconType = award.icon?.toLowerCase() || award.type?.toLowerCase() || 'default';
    return AWARD_ICONS[iconType] || AWARD_ICONS.default;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleViewAll = () => {
    navigate('/profile?tab=awards');
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <FaMedal className={styles.headerIcon} />
            <h3 className={styles.headerTitle}>My Awards</h3>
          </div>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaMedal className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>My Awards</h3>
        </div>
        {filteredAwards.length > 0 && (
          <span className={styles.count}>{filteredAwards.length}</span>
        )}
      </div>

      <div className={styles.content}>
        {filteredAwards.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>🏅</span>
            <p>No awards yet</p>
            <span className={styles.emptyHint}>Keep up the great work!</span>
          </div>
        ) : (
          <>
            <div className={styles.awardsList}>
              {filteredAwards.slice(0, 3).map((award, index) => (
                <div key={award._id || index} className={styles.awardCard}>
                  <span className={styles.awardIcon}>{getAwardIcon(award)}</span>
                  <div className={styles.awardInfo}>
                    <span className={styles.awardName}>
                      {award.name || award.title || 'Award'}
                    </span>
                    <div className={styles.awardMeta}>
                      <span className={styles.awardDate}>
                        {formatDate(award.awardedAt || award.createdAt)}
                      </span>
                      {award.programName && (
                        <span className={styles.awardProgram}>
                          • {award.programName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredAwards.length > 3 && (
              <button className={styles.viewAllBtn} onClick={handleViewAll}>
                View all {filteredAwards.length} awards <FaArrowRight />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlayerAwardsWidget;

